import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Eye } from "lucide-react";
import { useSavedLooks } from "../contexts/SavedLooksContext";

/**
 * ProductCard with intelligent availability badges
 * 
 * State A (Date only): 
 *   - Available (4 OR 8) → 🟢 Available
 *   - Neither available → 🔴 Unavailable + "Next available from [Date]"
 * 
 * State B (Date + Duration):
 *   - Selected duration available → 🟢 Available
 *   - 8-day selected but only 4-day available → 🟠 Limited Availability + "Available for shorter rental"
 *   - Neither available → 🔴 Unavailable + "Next available from [Date]"
 */
export default function ProductCard({ outfit, availability, hasDate, hasDuration, onQuickView, buildLookMode, buildLookSlot }) {
  const images = outfit.images?.length > 0 ? outfit.images : [""];
  const focalPoints = outfit.image_focal_points || {};
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [imgLoaded, setImgLoaded] = useState({});
  const intervalRef = useRef(null);
  const scrollRef = useRef(null);
  const { toggleSave, isSaved } = useSavedLooks();
  const liked = isSaved(outfit.id);
  
  // Build the link URL - use slug if available, append buildLook params if in Build Look mode
  const outfitPath = outfit.slug ? `/outfit/${outfit.slug}` : `/outfit/${outfit.id}`;
  const linkUrl = buildLookMode && buildLookSlot 
    ? `${outfitPath}?buildLook=true&slot=${buildLookSlot}`
    : outfitPath;

  // Determine availability status
  const getAvailabilityStatus = () => {
    if (!hasDate || !availability) return null;
    
    // Availability object: { avail4, avail8, status, nextAvailable }
    if (typeof availability === 'object' && availability.status) {
      return availability;
    }
    
    // Legacy simple boolean
    if (typeof availability === 'boolean') {
      return { status: availability ? 'available' : 'unavailable' };
    }
    
    return null;
  };
  
  const availStatus = getAvailabilityStatus();

  // Desktop: auto-rotate images on hover
  const startSlideshow = useCallback(() => {
    if (images.length <= 1) return;
    setIsHovering(true);
    setCurrentIdx(prev => (prev + 1) % images.length);
    intervalRef.current = setInterval(() => {
      setCurrentIdx(prev => (prev + 1) % images.length);
    }, 1800);
  }, [images.length]);

  const stopSlideshow = useCallback(() => {
    setIsHovering(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCurrentIdx(0);
  }, []);

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onQuickView?.(outfit);
  };

  // Mobile: snap scroll
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.offsetWidth);
    setCurrentIdx(idx);
  }, []);

  // Render availability badge
  const renderBadge = () => {
    if (!hasDate || !availStatus || !outfit.available) return null;
    
    const { status } = availStatus;
    
    if (status === 'available') {
      return (
        <div className="absolute top-3 left-3 z-10 w-2.5 h-2.5 rounded-full bg-[#4A7C59] shadow-sm ring-2 ring-white/60" data-testid={`badge-available-${outfit.id}`} />
      );
    }
    
    if (status === 'limited') {
      return (
        <div className="absolute top-3 left-3 z-10 w-2.5 h-2.5 rounded-full bg-[#D4A574] shadow-sm ring-2 ring-white/60" data-testid={`badge-limited-${outfit.id}`} />
      );
    }
    
    if (status === 'unavailable') {
      return (
        <div className="absolute top-3 left-3 z-10" data-testid={`badge-unavailable-${outfit.id}`}>
          <div className="px-2.5 py-1 text-[9px] tracking-[0.1em] uppercase bg-[#8B4513]/85 text-white">
            Unavailable
          </div>
          {availStatus.nextAvailable && (
            <div className="mt-0.5 px-2 py-0.5 text-[8px] bg-white/90 text-[#676664]">
              Next available from {availStatus.nextAvailable}
            </div>
          )}
        </div>
      );
    }
    
    return null;
  };

  // Overlay for unavailable
  const renderOverlay = () => {
    if (!outfit.available) {
      return (
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10">
          <span className="text-white text-[10px] tracking-[0.2em] uppercase bg-black/50 px-4 py-2">Reserved</span>
        </div>
      );
    }
    
    if (hasDate && availStatus?.status === 'unavailable') {
      return <div className="absolute inset-0 bg-white/30 z-[5]" />;
    }
    
    return null;
  };

  return (
    <div className="relative" data-testid={`product-card-${outfit.id}`}>
      <Link to={linkUrl} className="block group">
        {/* Desktop: Fade slideshow */}
        <div
          className="relative overflow-hidden bg-[#f7f5f4] hidden md:block"
          style={{ height: 0, paddingBottom: "150%" }}
          onMouseEnter={startSlideshow}
          onMouseLeave={stopSlideshow}
        >
          {images.map((img, i) => (
            <img
              key={i}
              src={img}
              alt={i === 0 ? `${outfit.name} for rent in Melbourne – Indian outfit hire` : `${outfit.name} view ${i + 1} – Indian outfit hire Melbourne`}
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out ${imgLoaded[i] ? '' : 'blur-sm scale-105'}`}
              style={{ opacity: i === currentIdx ? 1 : 0, objectPosition: `${focalPoints[img]?.x ?? 50}% ${focalPoints[img]?.y ?? 50}%` }}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              onLoad={() => setImgLoaded(prev => ({ ...prev, [i]: true }))}
              onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }}
            />
          ))}

          {/* Availability badge */}
          {renderBadge()}
          
          {/* Overlay */}
          {renderOverlay()}

          {/* Wishlist — top right */}
          <button
            className={`absolute top-3 right-3 w-8 h-8 bg-white/80 hover:bg-white flex items-center justify-center transition-all z-10 ${liked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSave(outfit.id); }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); toggleSave(outfit.id); }}
            data-testid={`wishlist-btn-${outfit.id}`}
          >
            <Heart className={`w-4 h-4 transition-colors ${liked ? 'fill-red-500 text-red-500' : 'text-[#282828]'}`} strokeWidth={1.5} />
          </button>

          {/* Quick View — bottom right eye icon */}
          <button
            onClick={handleQuickView}
            className="absolute bottom-3 right-3 w-8 h-8 bg-white/80 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
            data-testid={`quick-view-btn-${outfit.id}`}
          >
            <Eye className="w-4 h-4 text-[#282828]" strokeWidth={1.5} />
          </button>

          {/* Image dots indicator (desktop) */}
          {images.length > 1 && isHovering && (
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1 z-10">
              {images.map((_, i) => (
                <div
                  key={i}
                  className={`w-1 h-1 rounded-full transition-all duration-300 ${
                    i === currentIdx ? "bg-white w-3" : "bg-white/50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Mobile: Swipe gallery */}
        <div className="relative overflow-hidden bg-[#f7f5f4] md:hidden" style={{ height: 0, paddingBottom: "160%" }}>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="absolute inset-0 flex overflow-x-auto snap-x snap-mandatory"
            style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
          >
            {images.map((img, i) => (
              <div key={i} className="flex-shrink-0 w-full h-full snap-center">
                <img
                  src={img}
                  alt={i === 0 ? `${outfit.name} for rent in Melbourne – Indian outfit hire` : `${outfit.name} view ${i + 1} – Indian outfit hire Melbourne`}
                  className={`w-full h-full object-cover transition-all duration-500 ${imgLoaded[`m${i}`] ? '' : 'blur-sm scale-105'}`}
                  style={{ objectPosition: `${focalPoints[img]?.x ?? 50}% ${focalPoints[img]?.y ?? 50}%` }}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  onLoad={() => setImgLoaded(prev => ({ ...prev, [`m${i}`]: true }))}
                  onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }}
                />
              </div>
            ))}
          </div>

          {/* Availability badge (mobile) */}
          {renderBadge()}
          
          {/* Overlay (mobile) */}
          {renderOverlay()}

          {/* Pagination dots (mobile) */}
          {images.length > 1 && (
            <div className="absolute bottom-2.5 left-0 right-0 flex justify-center gap-1 z-10">
              {images.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    i === currentIdx ? "bg-white w-3" : "bg-white/40"
                  }`}
                />
              ))}
            </div>
          )}

          {/* Quick View — mobile: eye icon bottom-right */}
          <button
            onClick={handleQuickView}
            className="absolute bottom-3 right-3 w-8 h-8 bg-white/80 flex items-center justify-center z-10"
            data-testid={`quick-view-mobile-${outfit.id}`}
          >
            <Eye className="w-4 h-4 text-[#282828]" strokeWidth={1.5} />
          </button>

          {/* Wishlist — mobile: top right */}
          <button
            className={`absolute top-3 right-3 w-8 h-8 bg-white/80 flex items-center justify-center z-10 ${liked ? 'opacity-100' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSave(outfit.id); }}
            data-testid={`wishlist-btn-mobile-${outfit.id}`}
          >
            <Heart className={`w-4 h-4 transition-colors ${liked ? 'fill-red-500 text-red-500' : 'text-[#282828]'}`} strokeWidth={1.5} />
          </button>
        </div>
      </Link>

      {/* Product info — editorial layout */}
      <Link to={outfitPath} className="block pt-3 pb-1">
        <p className="text-[14px] text-[#1A1A1A] font-medium truncate" data-testid={`product-name-${outfit.id}`}>{outfit.name}</p>
        <p className="text-[12px] text-[#5A5A5A] mt-1.5" data-testid={`product-price-${outfit.id}`}>
          From <span className="text-[#9A7B4F] text-[15px] font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>${(() => {
            const prices = [outfit.price_4_day, outfit.price_8_day, outfit.price_per_day, ...(outfit.rental_options || []).flatMap(o => [o.price_4_day, o.price_8_day, o.price_per_day])].filter(p => p > 0);
            return prices.length > 0 ? Math.min(...prices) : 0;
          })()}</span>
        </p>
      </Link>
    </div>
  );
}
